// ── Anti-Larsen — limiteur relatif 7 bandes sur sortie ──
//
// 7 EQ peaking independants, 1 par bande (.1kHz - 16kHz).
// Toutes les 100ms, pour chaque bande :
//   avg   = moyenne des voisins existants
//   delta = bande - avg
//   si delta > 0 : gain = -delta
//   sinon        : gain = 0
// Mesure d'abord, correction ensuite (post mesure).

export const BANDS = [
  { label: '.1-.5', lo: 100, hi: 500 },
  { label: '.5-1k', lo: 500, hi: 1000 },
  { label: '1-2k',  lo: 1000, hi: 2000 },
  { label: '2-4k',  lo: 2000, hi: 4000 },
  { label: '4-8k',  lo: 4000, hi: 8000 },
  { label: '8-12k', lo: 8000, hi: 12000 },
  { label: '12-16k',lo: 12000, hi: 16000 },
] as const

// EQ configs for all 7 bands
const EQ_CONFIGS = [
  { idx: 0, freq: 300,   q: 1.0 },  // .1-.5 kHz
  { idx: 1, freq: 750,   q: 1.0 },  // .5-1 kHz
  { idx: 2, freq: 1500,  q: 1.0 },  // 1-2 kHz
  { idx: 3, freq: 3000,  q: 1.0 },  // 2-4 kHz
  { idx: 4, freq: 6000,  q: 1.0 },  // 4-8 kHz
  { idx: 5, freq: 10000, q: 1.0 },  // 8-12 kHz
  { idx: 6, freq: 14000, q: 1.0 },  // 12-16 kHz
]

export interface OutputEqNodes {
  analyser: AnalyserNode
  eqs: BiquadFilterNode[]  // 7 EQ filters
  remoteGain: GainNode     // volume control for incoming remote audio
  outputGain: GainNode     // volume control for speaker output
  ctx: AudioContext
  bandBins: { lo: number; hi: number }[]
  fftData: Float32Array<ArrayBuffer>
}

export function createOutputEqNodes(ctx: AudioContext): OutputEqNodes {
  const analyser = ctx.createAnalyser()
  analyser.fftSize = 2048
  analyser.smoothingTimeConstant = 0.5

  const remoteGain = ctx.createGain()
  remoteGain.gain.value = 1.0

  const outputGain = ctx.createGain()
  outputGain.gain.value = 1.0

  // 7 independent peaking EQs
  const eqs = EQ_CONFIGS.map(c => {
    const eq = ctx.createBiquadFilter()
    eq.type = 'peaking'
    eq.frequency.value = c.freq
    eq.Q.value = c.q
    eq.gain.value = 0
    return eq
  })

  const binHz = ctx.sampleRate / analyser.fftSize
  const bandBins = BANDS.map(b => ({
    lo: Math.floor(b.lo / binHz),
    hi: Math.ceil(b.hi / binHz),
  }))

  return { analyser, eqs, remoteGain, outputGain, ctx, bandBins, fftData: new Float32Array(analyser.frequencyBinCount) }
}

/** Read 7 bands energy from any AnalyserNode (standalone, no OutputEqNodes needed) */
export function readBandsFromAnalyser(analyser: AnalyserNode): { label: string; energy: number }[] {
  const fftData = new Float32Array(analyser.frequencyBinCount)
  analyser.getFloatFrequencyData(fftData)
  const binHz = analyser.context.sampleRate / analyser.fftSize
  return BANDS.map(b => {
    const lo = Math.floor(b.lo / binHz)
    const hi = Math.ceil(b.hi / binHz)
    let sum = 0, count = 0
    for (let j = lo; j <= hi && j < fftData.length; j++) {
      sum += fftData[j]; count++
    }
    return { label: b.label, energy: count > 0 ? Math.round(sum / count) : -128 }
  })
}

/** Read all 7 bands energy (dBFS) */
export function readBands(nodes: OutputEqNodes): { label: string; energy: number }[] {
  nodes.analyser.getFloatFrequencyData(nodes.fftData)
  return nodes.bandBins.map((bin, i) => {
    let sum = 0
    let count = 0
    for (let j = bin.lo; j <= bin.hi && j < nodes.fftData.length; j++) {
      sum += nodes.fftData[j]
      count++
    }
    return { label: BANDS[i].label, energy: count > 0 ? Math.round(sum / count) : -128 }
  })
}

/**
 * Compute and apply corrections for all 7 bands.
 * Edge bands use only their single neighbor as reference.
 * Returns 7 gains applied.
 */
export function applyCorrections(
  nodes: OutputEqNodes,
  bands: { energy: number }[],
): number[] {
  const gains: number[] = []
  for (let e = 0; e < EQ_CONFIGS.length; e++) {
    const { idx } = EQ_CONFIGS[e]
    const hasLeft = idx > 0
    const hasRight = idx < bands.length - 1
    let avg: number
    if (hasLeft && hasRight) {
      avg = (bands[idx - 1].energy + bands[idx + 1].energy) / 2
    } else if (hasLeft) {
      avg = bands[idx - 1].energy
    } else {
      avg = bands[idx + 1].energy
    }
    const delta = bands[idx].energy - avg
    const gain = delta > 0 ? -delta : 0
    nodes.eqs[e].gain.value = gain
    gains.push(Math.round(gain))
  }
  return gains
}
